import {
  type SearchableField,
  SearchableFieldGroup,
  SearchableFieldType,
} from '@framework/ui/components/search/ISearchableField.tsx';
import { useMemo } from 'react';
import {
  RegisterRecordStateSelectInputItem,
  registerRecordStateChipValue,
} from '../component/search/StateSelectInput.tsx';
import { type AlboConfigurazioneTipoDTO as AlboConfigurazioneTipoDTOBo } from '../configuration/api/schemas/alboConfigurazioneTipoDTO.ts';
import { EnumAlboConfigurazioneTipoCampoDTOTipo as EnumAlboConfigurazioneTipoCampoDTOTipoBo } from '../configuration/api/schemas/enumAlboConfigurazioneTipoCampoDTOTipo.ts';
import { type AlboConfigurazioneTipoDTO as AlboConfigurazioneTipoDTOFo } from '../frontoffice/api/schemas/alboConfigurazioneTipoDTO.ts';
import { EnumAlboConfigurazioneTipoCampoDTOTipo as EnumAlboConfigurazioneTipoCampoDTOTipoFo } from '../frontoffice/api/schemas/enumAlboConfigurazioneTipoCampoDTOTipo.ts';
import { type RegisterRecordFieldVisibility } from '../schema/RegisterRecordFieldVisibility.ts';

/**
 * Hook per generare i campi di ricerca (statici e dinamici) per un tipo di albo.
 * @param registerRecordType La configurazione del tipo di albo.
 */
export function useRegisterRecordSearchableFields(
  registerRecordType:
    | AlboConfigurazioneTipoDTOBo
    | AlboConfigurazioneTipoDTOFo
    | null,
  registerRecordFlagVisibility: RegisterRecordFieldVisibility,
) {
  const searchableFields: SearchableField[] = useMemo(() => {
    if (!registerRecordType) return [];

    // --- CAMPI DI RICERCA STATICI ---
    const staticFields: SearchableField[] = [
      {
        type: SearchableFieldType.Custom,
        label: 'Stato',
        name: 'statoIscrizione',
        renderValue: registerRecordStateChipValue(
          registerRecordType.stati || [],
        ),
        input: (
          <RegisterRecordStateSelectInputItem
            name="statoIscrizione"
            states={registerRecordType.stati || []}
            placement="topLeft"
          />
        ),
        group: SearchableFieldGroup.Filters,
      },
      {
        type: SearchableFieldType.NumberRange,
        name: 'numero',
        label: 'Numero',
        from: {
          name: 'numeroDa',
          label: 'Da Numero',
        },
        to: {
          name: 'numeroA',
          label: 'A Numero',
        },
        group: SearchableFieldGroup.Filters,
      },
      {
        type: SearchableFieldType.DateRange,
        name: 'dataEmissione',
        label: 'Data Inizio',
        from: {
          name: 'dataEmissioneDa',
          label: 'Da Data Inizio',
        },
        to: {
          name: 'dataEmissioneA',
          label: 'A Data Inizio',
        },
        group: SearchableFieldGroup.Filters,
      },
      {
        type: SearchableFieldType.DateRange,
        name: 'dataScadenza',
        label: 'Data Fine',
        from: {
          name: 'dataScadenzaDa',
          label: 'Da Data Fine',
        },
        to: {
          name: 'dataScadenzaA',
          label: 'A Data Fine',
        },
        group: SearchableFieldGroup.Filters,
      },
    ];

    const getFieldType = (
      dtoType:
        | EnumAlboConfigurazioneTipoCampoDTOTipoBo
        | EnumAlboConfigurazioneTipoCampoDTOTipoFo
        | undefined,
    ): SearchableFieldType => {
      switch (dtoType) {
        case EnumAlboConfigurazioneTipoCampoDTOTipoBo.DATE:
        case EnumAlboConfigurazioneTipoCampoDTOTipoFo.DATE:
          return SearchableFieldType.Date;
        case EnumAlboConfigurazioneTipoCampoDTOTipoBo.SELECT:
        case EnumAlboConfigurazioneTipoCampoDTOTipoFo.SELECT:
          return SearchableFieldType.Select;
        case EnumAlboConfigurazioneTipoCampoDTOTipoBo.NUMBER:
        case EnumAlboConfigurazioneTipoCampoDTOTipoFo.NUMBER:
          return SearchableFieldType.NumberRange;
        default:
          return SearchableFieldType.Text;
      }
    };

    // --- CAMPI DI RICERCA DINAMICI ---
    const dynamicFields: SearchableField[] =
      registerRecordType.configurazioneCampi
        ?.filter(campo => campo[registerRecordFlagVisibility] && campo.codice)
        .map(campo => {
          // Mappiamo il tipo di campo al tipo di SearchableField
          const fieldType = getFieldType(campo.tipo!);

          if (fieldType === SearchableFieldType.Select) {
            return {
              type: fieldType,
              name: `${campo.codice}`,
              label: campo.etichetta,
              placeholder: `${campo.etichetta}...`,
              group: SearchableFieldGroup.Filters,
              options: campo.opzioni?.map(op => ({
                label: op,
                value: op,
              })),
            } as SearchableField;
          }

          if (fieldType === SearchableFieldType.NumberRange) {
            return {
              type: fieldType,
              name: `${campo.codice}`,
              label: campo.etichetta,
              from: {
                name: `${campo.codice}Da`,
                label: `Da ${campo.etichetta}`,
              },
              to: {
                name: `${campo.codice}A`,
                label: `A ${campo.etichetta}`,
              },
              group: SearchableFieldGroup.Filters,
            } as SearchableField;
          }

          return {
            type: fieldType,
            name: `${campo.codice}`,
            label: campo.etichetta,
            placeholder: `${campo.etichetta}...`,
            group: SearchableFieldGroup.Filters,
          } as SearchableField;
        }) ?? [];

    return [...staticFields, ...dynamicFields];
  }, [registerRecordType, registerRecordFlagVisibility]);

  return searchableFields;
}
